import style from './LoginButton.module.scss';
import cx from 'clsx';

import { FC, HTMLAttributes, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'util/Formatting';
import { CountryCode } from 'i18n/types';

import { Icon } from 'components/Icon';
import { Drawer } from 'components/Drawer/Drawer';
import { useCustomer } from 'hooks/context/customer/customer';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { useMerchant } from 'hooks/context/merchant';
import { LOGIN_BUTTON_TEST_ID, LOGIN_BUTTON } from './LoginButton.const';
import { SessionStorage } from 'util/Storage';
import { COLO_CONTACT_INFO, COLO_CONTACT_NOTE } from 'routes/Checkout/context/OrderDetails';
import { useHandleCloseDrawer } from 'hooks/useHandleCloseDrawer';
import { CONSUMER_PROFILE_ENABLED } from 'hooks/context/merchant/models';

type LoginButtonProps = HTMLAttributes<HTMLButtonElement>;

export const LoginButton: FC<LoginButtonProps> = ({ className, ...props }) => {
  const { t } = useTranslation();
  const { isSettingEnabled, country } = useMerchant();
  const { isLoggedIn, clearToken, customer, setLogOutButtonClicked } = useCustomer();
  const navigate = useSmartNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const loginWrapperRef = useRef<HTMLDivElement>(null);
  const customerName = `${customer?.firstName} ${customer?.lastName ? `${customer?.lastName.substring(0,1).toUpperCase()}.`: ''}`;
  const phoneNumber = customer?.phoneNumber?.verified && customer?.phoneNumber?.value && parsePhoneNumber(customer?.phoneNumber?.value, country as CountryCode).format('NATIONAL');
  const alternativeName = `${customer?.emailAddress?.verified ? customer?.emailAddress?.value : phoneNumber}`;
  const displayName = `${customerName.includes('undefined') ? alternativeName : customerName }`;
  const isLoading = displayName.includes('undefined');

  const handleClick = () => {
    if (isLoggedIn) {
      setIsOpen(current => !current);
    } else {
      navigate('~login/info', { replace: true, state: { redirectPath:'~menu' } });
    }
  };

  // Handle closing on outside mouse clicks
  useHandleCloseDrawer(loginWrapperRef, () => { setIsOpen(false); });

  const logout = () => {
    setIsOpen(false);
    clearToken();
    setLogOutButtonClicked(true);
    // Clear Contact Info
    SessionStorage.tryRemoveItem(COLO_CONTACT_INFO);
    SessionStorage.tryRemoveItem(COLO_CONTACT_NOTE);
  };

  return (
    <div ref={loginWrapperRef}>
      <button
        id={LOGIN_BUTTON.trigger}
        onClick={handleClick}
        className={cx(style.loginButton, className)}
        {...props}
      >
        <span className={cx(style.buttonTextWrapper)}>
          <Icon className={cx(style.icon)} name={'avatar'} />
          <p data-testid={LOGIN_BUTTON_TEST_ID.button}
            className={cx(style.buttonText)}
          >
            {isLoggedIn && !isLoading ? displayName : t('login')}
          </p>
        </span>
      </button>
      <Drawer
        isOpen={isOpen}
        triggerElement={LOGIN_BUTTON.trigger}
        className={style.logoutDrawer}
        onClose={() => setIsOpen(false)}
      >
        <ul>
          {isSettingEnabled(CONSUMER_PROFILE_ENABLED) &&
          <li>
            <a className={cx(style.dropdownBtn)} href="/customer-profile" data-testid={LOGIN_BUTTON_TEST_ID.account} >
              {t('account')}
            </a>
          </li>}
          <li>
            <button className={cx(style.dropdownBtn)} data-testid={LOGIN_BUTTON_TEST_ID.logout} onClick={logout}>
              {t('logout')}
            </button>
          </li>
        </ul>
      </Drawer>
    </div>
    );
  };
